import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocalStorage } from "usehooks-ts";

import { getCart, postCart } from "@/api/cart";
import { NotificationItemAdded } from "@/components/NotificationItemAdded";
import { useProfile } from "@/hooks/profile";
import useAnalytics from "@/hooks/useAnalytics";
import useBreakpointTest from "@/hooks/useBreakpointTest";
import useNotification from "@/hooks/useNotification";
import { authStatusFromProfileQuery } from "@/hooks/useProfile";

import {
  addCartItem,
  cartsAreEqual,
  removeCartItem,
  updateCartItemQuantity,
} from "./helpers";

export type CartItem = {
  id: number;
  quantity: number;
  price: number;
};

export type Cart = {
  products: CartItem[];
  dispensers: CartItem[];
};

export type CartItemCategory = keyof Cart;

type CartContextValue = {
  cart: Cart;
  addItem: (item: CartItem, category: CartItemCategory) => void;
  removeItem: (itemId: CartItem["id"]) => void;
  updateItem: (item: CartItem, category: CartItemCategory) => void;
  clearCart: () => void;
  filterCart: () => void;
};

const SYNC_CART_DEBOUNCE_TIMER = 500;
const INITIAL_CART_STATE: Cart = {
  products: [],
  dispensers: [],
};

export const CartContext = createContext<CartContextValue>(
  {} as unknown as CartContextValue
);

export function CartProvider({ children }: { children: ReactNode }) {
  const profile = useProfile();
  const { isLoggedIn } = authStatusFromProfileQuery(profile);
  const [cart, setCart] = useLocalStorage<Cart>("cart", INITIAL_CART_STATE);
  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const { trackAddToCartEvent } = useAnalytics();
  const { showNotification } = useNotification();
  const isDesktop = useBreakpointTest("md");

  useEffect(() => {
    if (isLoggedIn) {
      checkCartOnLogIn();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      const newTimeoutId = window.setTimeout(() => {
        updateRemoteCart();
      }, SYNC_CART_DEBOUNCE_TIMER);
      setTimeoutId(newTimeoutId);
    }

    // Don’t cleanup the timeout on unmount
    // because we still want the sync to send
  }, [cart, isLoggedIn]);

  /**
   * Posts the current state of the cart to the server
   */
  const updateRemoteCart = async (retries = 3) => {
    if (retries < 0) {
      throw new Error("Failed to sync cart");
    }

    try {
      const cartResponse = await postCart(cart);
      const { products, dispensers } = cartResponse;

      const remoteCart = {
        products,
        dispensers,
      };

      if (cartsAreEqual(cart, remoteCart)) {
        return;
      } else {
        console.info("Retrying sync cart");
        updateRemoteCart(retries - 1);
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * If the local cart is empty, fetch the remote cart and update the local cart with it.
   *
   * The UX decision is that an "anonymous shopping" cart should take priority over a cart from a previous session
   */
  const checkCartOnLogIn = async () => {
    /** @todo Test users (api/admin/test-user) don't have carts, so the logic explodes a bit without the ? */
    const cartHasItems =
      cart.products?.length > 0 || cart.dispensers?.length > 0;

    if (cartHasItems) {
      return;
    }

    try {
      const newCart = await getRemoteCart();

      setCart(newCart);
    } catch (error) {
      console.error(error);
    }
  };

  const getRemoteCart = useCallback(async () => {
    const { products, dispensers } = await getCart();

    return {
      products,
      dispensers,
    };
  }, []);

  /**
   * Update the local cart from the remote cart. This is to filter out any unavailable or out of stock items.
   */
  const filterCart = useCallback(async () => {
    try {
      const remoteCart = await getRemoteCart();
      setCart(remoteCart);
    } catch (error) {
      console.error(error);
    }
  }, [getRemoteCart]);

  /**
   * Add an item to the cart. If the item is already in the cart, increase the quantity.
   *
   * @param item The item to add to the cart.
   * @returns void
   * @example
   * addItem({
   *   id: 1,
   *   quantity: 1,
   *   price: 10,
   *   type: "products"
   * });
   *
   */
  const addItem = useCallback(
    (item: CartItem, category: CartItemCategory) => {
      trackAddToCartEvent(item.price, "NOK", item.id);

      const newCart = addCartItem(cart, item, category);

      setCart(newCart);

      showNotification(<NotificationItemAdded />, isDesktop ? "top" : "bottom");
    },
    [cart, showNotification, isDesktop]
  );

  /**
   * Remove an item from the cart.
   * @param itemId The id of the item to remove.
   * @returns void
   * @example
   * removeItem(31);
   */
  const removeItem = useCallback(
    (itemId: CartItem["id"]) => {
      const newCart = removeCartItem(cart, itemId);

      setCart(newCart);
    },
    [cart]
  );

  /**
   * Set the value of a cart item.
   * If the item is in the cart, it’s values will be overwritten with those given
   * If the item is not in the cart, nothing will happen.
   * If the quantity is <=0, the item will be removed from the cart.
   * @param item The item to set.
   * @returns void
   * @example
   * updateItem({
   *   id: 1,
   *   quantity: 7,
   *   price: 10,
   *   type: "products"
   * });
   */
  const updateItem = useCallback(
    (item: CartItem, category: CartItemCategory) => {
      const newCart = updateCartItemQuantity(cart, item, category);

      setCart(newCart);
    },
    [cart]
  );

  /**
   * Clear the cart.
   */
  const clearCart = useCallback(() => {
    setCart(INITIAL_CART_STATE);
  }, []);

  const contextValue = useMemo(
    () => ({
      cart,
      addItem,
      removeItem,
      updateItem,
      clearCart,
      filterCart,
    }),
    [cart]
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
}
